import { Dispatch, SetStateAction } from 'react';
import { UserProgress, LanguageCode, LearnedWord, WordProgress } from '../types/types';
import { 
  getLanguageData, 
  wolofCategories,
  bambaraCategories,
 
} from '../data/languages';
import { DEFAULT_SETTINGS } from './revisionHelpers';
import supabaseProgressService from '../services/supabaseProgressService';

// Constante locale pour remplacer DEFAULT_LANGUAGE
const FALLBACK_LANGUAGE: LanguageCode = 'wf'; // Wolof comme langue de fallback

export interface ResetProgressOptions {
  setCompletedCategories: Dispatch<SetStateAction<string[]>>;
  setUserProgress: Dispatch<SetStateAction<UserProgress>>;
  setQuizScore: Dispatch<SetStateAction<number>>;
  user: any | null;
  saveDataToGoogleDrive: () => Promise<void>;
  currentLanguage: LanguageCode;
}

// DEFAULT_USER_PROGRESS devient une fonction
export const createDefaultUserProgress = (language: LanguageCode): UserProgress => ({
  learnedWords: new Set<string>(),
  wordsToReview: new Set<string>(),
  wordProgress: {},
  recentlyLearnedWords: [],
  language: language
});

// Export pour compatibilité avec le reste du code
export const DEFAULT_USER_PROGRESS = createDefaultUserProgress(FALLBACK_LANGUAGE);

interface ProgressState {
  completedCategories: string[];
  userProgress: UserProgress;
  quizScore: number;
}

interface SavedProgress {
  learnedWords: string[];
  wordsToReview: string[];
  wordProgress: Record<string, WordProgress>;
  recentlyLearnedWords: LearnedWord[];
  revisionSettings: typeof DEFAULT_SETTINGS;
  language: LanguageCode;
  experience?: number;
  streak?: number;
  totalXP?: number;
}

// Vérification de la disponibilité du localStorage
const checkStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    console.error('LocalStorage non disponible:', e);
    return false;
  }
};

// Fonction auxiliaire pour sauvegarder avec retry (conservée pour clearAllStatsData)
const saveWithRetry = async (key: string, value: any, maxRetries: number = 3): Promise<boolean> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
      return true;
    } catch (error) {
      console.error(`Tentative ${i + 1}/${maxRetries} échouée pour ${key}:`, error);
      if (i === maxRetries - 1) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
    }
  }
  return false;
};

// Fonction pour obtenir les catégories d'une langue
const getCategoriesForLanguage = (languageCode: LanguageCode) => {
  switch (languageCode) {
    case 'wf':
      return wolofCategories;
    case 'ba':
      return bambaraCategories;
    default:
      return getLanguageData(languageCode).categories;
  }
};

// Fonction principale de nettoyage - supprime TOUTES les données liées à une langue
const clearAllProgressData = async (languageCode: LanguageCode): Promise<void> => {
  if (!checkStorageAvailable()) return;

  console.log(`🗑️ Suppression de toutes les données pour ${languageCode}...`);

  // Clés générales à supprimer
  const keysToRemove = [
    'completedCategories',
    'categoryProgress',
    'quizScore',
    'currentWordIndex',
    'userProgress',
    'learnedWords',
    'wordsToReview',
    'wordProgress',
    'recentlyLearnedWords',
    'sentenceConstructionProgress',
    'sentenceGapProgress',
    'grammarProgress',
    'reviewProgress',
    'lastLearnedCategory',
    'lastLearnedWord',
    'currentLearningSession',
    'learningStats',
    'testScores',
    'revisionSettings',
    'totalXP',
    'progress'
  ];

  // Clés spécifiques à la grammaire
  const grammarKeys = [
    'grammar-progress',
    'grammar-metadata',
    'grammar-rules',
    'grammar-history',
    'grammar-settings',
    'grammar-subcategories',
    'grammar-mastered',
    'grammar-revision'
  ];

  // Clés d'historique de révision
  const revisionHistoryKeys = [
    'sessionHistory',
    'revisionHistory',
    'revisionProgress',
    'reviewHistory',
    'reviewProgress',
    'reviewSessions',
    'revisionSessions',
    'revisionStats',
    'reviewStats'
  ];

  // Ajouter les clés de grammaire et d'historique à la liste générale
  keysToRemove.push(...grammarKeys, ...revisionHistoryKeys);

  // Nettoyer toutes les variations possibles des clés
  console.log("🧹 Suppression des clés avec différents formats...");
  for (const key of keysToRemove) {
    // Différents formats de clés possibles
    const keyVariations = [
      `${languageCode}_${key}`,
      `${languageCode}-${key}`,
      `${key}_${languageCode}`,
      `${key}-${languageCode}`,
      `grammar_${languageCode}_${key}`,
      `grammar-${languageCode}-${key}`,
      `${languageCode}_grammar_${key}`,
      `${languageCode}-grammar-${key}`,
      `revision_${languageCode}_${key}`,
      `revision-${languageCode}-${key}`,
      `${languageCode}_revision_${key}`,
      `${languageCode}-revision-${key}`
    ];

    for (const variation of keyVariations) {
      localStorage.removeItem(variation);
    }
  }

  // Supprimer les clés spécifiques aux catégories
  const categories = getCategoriesForLanguage(languageCode);
  Object.keys(categories).forEach(category => {
    const categoryKeys = [
      `${languageCode}_learnState_${category}`,
      `${languageCode}_currentWordIndex_${category}`,
      `${languageCode}_testProgress_${category}`,
      `${languageCode}-${category}-sessionLearned`,
      `${languageCode}-${category}-inProgress`
    ];
    
    categoryKeys.forEach(key => localStorage.removeItem(key));
    
    // Gestion spéciale pour la catégorie Grammaire avec sous-catégories
    if (category === 'Grammaire') {
      const grammarCategory = categories[category];
      if (grammarCategory && typeof grammarCategory === 'object') {
        Object.keys(grammarCategory).forEach(subcategory => {
          const subcategoryKeys = [
            `${languageCode}_learnState_${category}_${subcategory}`,
            `${languageCode}_currentWordIndex_${category}_${subcategory}`,
            `${languageCode}_testProgress_${category}_${subcategory}`,
            `${languageCode}-${category}-${subcategory}-sessionLearned`,
            `${languageCode}-${category}-${subcategory}-inProgress`
          ];
          subcategoryKeys.forEach(key => localStorage.removeItem(key));
        });
      }
    }
  });

  // Nettoyage des données de statistiques
  await clearAllStatsData(languageCode);

  console.log(`✅ Suppression des données de progression pour ${languageCode} terminée.`);
};

// Nettoyage spécifique des données de statistiques
const clearAllStatsData = async (languageCode: LanguageCode): Promise<void> => {
  if (!checkStorageAvailable()) return;

  console.log(`🧹 Nettoyage des données de statistiques pour ${languageCode}...`);

  // Clés spécifiques aux statistiques et données de progression
  const statsKeys = [
    // Données de grammaire
    `grammar-progress-${languageCode}`,
    `${languageCode}-grammar-progress`,
    `grammar_progress_${languageCode}`,
    `${languageCode}_grammar_progress`,
    
    // Données de construction de phrases
    `sentence-construction-progress-${languageCode}`,
    `${languageCode}-sentence-construction-progress`,
    `sentenceConstruction-${languageCode}`,
    `${languageCode}-sentenceConstruction`,
    
    // Données de phrases à trous
    `sentence-gap-progress-${languageCode}`,
    `${languageCode}-sentence-gap-progress`,
    
    // Listes globales de mots appris
    `${languageCode}-allLearnedWords`,
    `${languageCode}_allLearnedWords`,
    `allLearnedWords-${languageCode}`,
    `allLearnedWords_${languageCode}`,
    
    // Données d'expérience et de niveau
    `${languageCode}-experience`,
    `${languageCode}_experience`,
    `experience-${languageCode}`,
    `experience_${languageCode}`,
    
    // Données de streak et temps d'apprentissage
    `${languageCode}-streak`,
    `${languageCode}_streak`,
    `${languageCode}-learningTime`,
    `${languageCode}_learningTime`,
    
    // Métadonnées de progression
    `${languageCode}-metadata`,
    `${languageCode}_metadata`,
    `metadata-${languageCode}`,
    `metadata_${languageCode}`,
    
    // Sessions et historique
    `${languageCode}-sessions`,
    `${languageCode}_sessions`,
    `sessions-${languageCode}`,
    `sessions_${languageCode}`,
    
    // Statistiques générales
    `${languageCode}-stats`,
    `${languageCode}_stats`,
    `stats-${languageCode}`,
    `stats_${languageCode}`,
    
    // Nouvelles clés pour le système de révision
    `revision-${languageCode}-progress`,
    `revision-${languageCode}-sessionHistory`,
    `${languageCode}-revision-progress`,
    `${languageCode}-revision-sessionHistory`,
    
    // Clés pour les modes d'apprentissage
    `learn-progress-${languageCode}`,
    `sentence-progress-${languageCode}`,
    `${languageCode}-learn-progress`,
    `${languageCode}-sentence-progress`,
    
    // Clés XP et progression générale
    `${languageCode}-totalXP`,
    `${languageCode}-completedCategories`,
    `${languageCode}-progress`,
    // Baseline XP journalier — doit être remis à zéro sinon l'objectif du jour
    // reste bloqué (getDailyXP compare totalXP à cet ancien baseline)
    `${languageCode}-daily-xp-start`,
    // Boucliers de streak — réinitialisés avec la progression
    'jogolinga-streak-shields',
  ];

  // Supprimer toutes les clés de statistiques
  statsKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`❌ Erreur lors de la suppression de ${key}:`, error);
    }
  });

  // Recherche et suppression de toutes les clés contenant des mots-clés de statistiques
  const statsPatterns = [
    'grammar',
    'sentence',
    'allLearnedWords',
    'sessionLearned',
    'inProgress',
    'experience',
    'streak',
    'learningTime',
    'metadata',
    'sessions',
    'stats',
    'revision',
    'learn',
    'totalXP',
    'progress'
  ];

  Object.keys(localStorage).forEach(key => {
    // Vérifier si la clé est liée à la langue ET contient un pattern de statistiques
    const isRelatedToLanguage = key.includes(languageCode);
    const containsStatsPattern = statsPatterns.some(pattern => 
      key.toLowerCase().includes(pattern.toLowerCase())
    );
    
    if (isRelatedToLanguage && containsStatsPattern) {
      console.log(`🔍 Trouvé et supprimé: ${key}`);
      localStorage.removeItem(key);
    }
  });
};

// ✅ NOUVELLE FONCTION: Suppression Supabase (progression + révisions)
const deleteDataFromSupabase = async (languageCode: LanguageCode): Promise<boolean> => {
  console.log("===== SUPPRESSION DES DONNÉES SUR SUPABASE =====");
  
  try {
    console.log(`🗑️ Suppression des données Supabase pour ${languageCode}...`);
    
    // 1️⃣ Supprimer la progression (user_progress)
    const progressDeleted = await supabaseProgressService.deleteProgress(languageCode);
    
    if (progressDeleted) {
      console.log("✅ Table user_progress supprimée");
    } else {
      console.warn("⚠️ Suppression user_progress échouée ou aucune donnée");
    }
    
    // 2️⃣ Supprimer les révisions (user_revision)
    console.log(`🗑️ Suppression de la table user_revision pour ${languageCode}...`);
    
    try {
      // Import dynamique pour éviter les dépendances circulaires
      const { supabase } = await import('../services/supabaseAudioService');
      
      // Récupérer la session utilisateur
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        console.warn("⚠️ Aucune session utilisateur - impossible de supprimer user_revision");
      } else {
        const { error: revisionError } = await supabase
          .from('user_revision')
          .delete()
          .eq('user_id', session.user.id)
          .eq('language_code', languageCode);
        
        if (revisionError) {
          console.error("❌ Erreur suppression user_revision:", revisionError);
        } else {
          console.log("✅ Table user_revision supprimée");
        }
      }
    } catch (revisionError) {
      console.error("❌ Erreur lors de la suppression user_revision:", revisionError);
    }
    
    // Retourner true si au moins une suppression a réussi
    return progressDeleted;
    
  } catch (error) {
    console.error("❌ Erreur lors de la suppression Supabase:", error);
    return false;
  }
};

// ✅ FONCTION OPTIMISÉE - Suppression Supabase + localStorage uniquement
export const resetProgress = async (options: ResetProgressOptions): Promise<void> => {
  const { 
    setCompletedCategories, 
    setUserProgress, 
    setQuizScore, 
    user, 
    saveDataToGoogleDrive, 
    currentLanguage 
  } = options;

  if (!checkStorageAvailable()) {
    throw new Error('LocalStorage non disponible');
  }

  try {
    console.log("===== DÉBUT RÉINITIALISATION COMPLÈTE =====");
    console.log('🌍 Langue courante:', currentLanguage);
    
    // Affichage de debug avant suppression
    console.log("📊 État du localStorage AVANT suppression:");
    Object.keys(localStorage).forEach(key => {
      if (key.includes(currentLanguage)) {
        console.log(`- ${key}: ${localStorage.getItem(key)?.slice(0, 100)}...`);
      }
    });

    // Reset des états React
    setCompletedCategories([]);
    setUserProgress(createDefaultUserProgress(currentLanguage));
    setQuizScore(0);

    // ✅ ÉTAPE 1: Suppression Supabase (en premier pour éviter re-sync)
    console.log("🗑️ ÉTAPE 1: Suppression Supabase...");
    const supabaseDeleted = await deleteDataFromSupabase(currentLanguage);
    
    if (supabaseDeleted) {
      console.log("✅ Données Supabase supprimées");
    } else {
      console.warn("⚠️ Suppression Supabase échouée ou pas de données");
    }

    // ✅ ÉTAPE 2: Nettoyage localStorage
    console.log("🗑️ ÉTAPE 2: Nettoyage localStorage...");
    await clearAllProgressData(currentLanguage);

    // Vérification après suppression
    console.log("📊 État du localStorage APRÈS suppression:");
    const remainingKeys = Object.keys(localStorage).filter(key => key.includes(currentLanguage));
    
    if (remainingKeys.length > 0) {
      console.log("⚠️ Clés restantes détectées, suppression forcée:");
      remainingKeys.forEach(key => {
        console.log(`🗑️ Suppression forcée de: ${key}`);
        localStorage.removeItem(key);
      });
    } else {
      console.log("✅ Toutes les données localStorage supprimées");
    }

    console.log("✅ Réinitialisation de la progression terminée avec succès");
    
    // Petit délai pour permettre la suppression complète
    console.log("⏳ Attente avant rechargement...");
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Rechargement de la page - les données par défaut seront créées au démarrage
    console.log("🔄 Rechargement de la page pour appliquer les changements");
    window.location.reload();
  } catch (error) {
    console.error('❌ Erreur lors de la réinitialisation:', error);
    throw error;
  } finally {
    console.log("===== FIN RÉINITIALISATION COMPLÈTE =====");
  }
};

export const checkResetNeeded = (userProgress: UserProgress): boolean => {
  return (
    userProgress.learnedWords.size > 0 ||
    userProgress.wordsToReview.size > 0 ||
    Object.keys(userProgress.wordProgress).length > 0 ||
    userProgress.recentlyLearnedWords.length > 0
  );
};

export const getCurrentProgress = (languageCode: LanguageCode): ProgressState => {
  if (!checkStorageAvailable()) {
    return {
      completedCategories: [],
      userProgress: createDefaultUserProgress(languageCode),
      quizScore: 0
    };
  }

  const defaultProgress: ProgressState = {
    completedCategories: [],
    userProgress: createDefaultUserProgress(languageCode),
    quizScore: 0
  };

  try {
    const savedUserProgress = localStorage.getItem(`${languageCode}-progress`);
    if (!savedUserProgress) return defaultProgress;

    const parsed = JSON.parse(savedUserProgress) as SavedProgress;
    const recentlyLearned = parsed.recentlyLearnedWords || [];
    
    const validRecentlyLearned: LearnedWord[] = recentlyLearned
      .map(word => {
        if (typeof word === 'string') {
          return {
            word,
            category: '',
            language: languageCode,
            timestamp: Date.now()
          };
        }
        return word as LearnedWord;
      })
      .filter((word): word is LearnedWord => Boolean(word));

    const completedCategories = JSON.parse(
      localStorage.getItem(`${languageCode}-completedCategories`) || '[]'
    );
    const quizScore = parseInt(
      localStorage.getItem(`${languageCode}-quizScore`) || '0'
    );

    return {
      completedCategories,
      userProgress: {
        learnedWords: new Set(parsed.learnedWords || []),
        wordsToReview: new Set(parsed.wordsToReview || []),
        wordProgress: parsed.wordProgress || {},
        recentlyLearnedWords: validRecentlyLearned,
        language: languageCode,
      },
      quizScore
    };
  } catch (error) {
    console.error('Erreur lors de la récupération de la progression:', error);
    return defaultProgress;
  }
};

export default {
  resetProgress,
  checkResetNeeded,
  getCurrentProgress,
  DEFAULT_USER_PROGRESS
};